import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import config from 'config';
import CamFeedbackModal from 'components/CamFeedbackModal';
import CamHighlightsPill from 'components/CamHighlightsPill';
import LoadingSpinner from 'components/CamPlayerKbygRewinds/LoadingSpinner/LoadingSpinner';
import FullscreenControl from 'components/FullscreenControl';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import PlayButtonOverlay from 'components/PlayButtonOverlay';
import VideoPlaylistPlayer, { type VideoPlayerHandle } from 'components/VideoPlaylistPlayer';

import { Camera } from 'types/camera';
import { SpotDetails } from 'types/spot';
import { Reel } from 'types/reelPlayer';

import ReelPlayerAds from './components/ReelPlayerAds';
import ReelBars from './components/ReelBars';
import EmptyReelsCTA from './components/EmptyReelsCTA';
import EndOfReelsCTA from './components/EndOfReelsCTA';
import ReelPlayerCamControls from './components/ReelPlayerCamControls';
import useCornerControls from './hooks/useCornerControls';

import styles from './ReelPlayer.module.scss';
import CamHostAd from 'components/CamHostAd/CamHostAd';

const ReelPlayer = ({
  reels = [],
  camera,
  spot,
  enableAds = false,
  enableCornerControls = false,
  enabledFeatures,
  segmentEvent = () => {},
  timezone,
  rewindsFetching,
  rewindFetchError,
  prerollEnabled = false,
}: {
  reels: Reel[];
  camera: Camera;
  spot: SpotDetails;
  enableAds?: boolean;
  enableCornerControls?: boolean;
  enabledFeatures: {
    wavePool: boolean;
  };
  segmentEvent?: (action: string, data?: { clipsPlayed: number; clipsAvailable: number }) => void;
  timezone: string;
  rewindsFetching?: boolean;
  rewindFetchError?: boolean;
  prerollEnabled?: boolean;
}) => {
  const clipsPlayed = useRef(0);
  const videoPlayerRef = useRef<VideoPlayerHandle>(null);
  const [hasClickedPlayOverlay, setHasClickedPlayOverlay] = useState(false);
  // Gates the play-overlay vs loading-overlay UI so the user can't click into the "ads still loading" window.
  const [prerollResolved, setPrerollResolved] = useState(!prerollEnabled);
  // Keeps highlights from racing the pre-roll. Flips true when the pre-roll is skipped or has ended.
  const [autoplayAllowed, setAutoplayAllowed] = useState(!prerollEnabled);
  // Distinguishes the initial onAdRunningChange(false) from a real true→false transition.
  const adWasRunningRef = useRef(false);
  // Set when the browser blocks the post-pre-roll play() — the original click's user activation has expired.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  // Calls adDisplayContainer.initialize() — invoked from the play-overlay click before the async VAST fetch.
  const initAdContainerRef = useRef<() => void>(() => {});

  const router = useRouter();
  const { clipIndex, native } = router.query;
  const isNative = native?.toString() === 'true';
  const upgradeUrl = isNative ? 'surfline://upgrade' : `${config.surflineHost}/upgrade`;

  const [isHighlightReelPlaying, setIsHighlightReelPlaying] = useState(false);
  const [adRunning, setAdRunning] = useState(false);
  const [currentReelIndex, setCurrentReelIndex] = useState(clipIndex ? Number(clipIndex) : 0);
  const [showReelPaywall, setShowReelPaywall] = useState(false);
  const [showHostAd, setShowHostAd] = useState(false);

  const reelUrls = useMemo(
    () =>
      reels.map((reel: Reel) =>
        reel.highlightUrl !== 'paywalled-highlight-reel' ? (reel.highlightUrl as string) : null,
      ),
    [reels],
  );
  const nonPaywalledReels = useMemo(
    () => reels.filter((reel: Reel) => reel.highlightUrl !== 'paywalled-highlight-reel'),
    [reels],
  );

  const isFirstReel = currentReelIndex <= 0 || (!rewindsFetching && reels.length <= 1);
  const isLastReel =
    currentReelIndex >= nonPaywalledReels.length - 1 || (!rewindsFetching && reels.length <= 1);
  const isPaywalledReel = useMemo(
    () => reels[currentReelIndex]?.highlightUrl === 'paywalled-highlight-reel',
    [reels, currentReelIndex],
  );

  const { fullscreenElementRef, isFullscreen, supportsFullscreen, handleRequestFullscreen } =
    useCornerControls({
      videoPlayerRef,
    });

  const handleSetCurrentReelIndex = useCallback(
    (index: number) => (index >= 0 && index < reels.length ? setCurrentReelIndex(index) : null),
    [reels.length],
  );

  // Counts clips the user has *started* watching this session. Paywalled reels don't count.
  const incrementClipsPlayedFor = useCallback(
    (index: number) => {
      if (reels[index]?.highlightUrl !== 'paywalled-highlight-reel') {
        clipsPlayed.current += 1;
      }
    },
    [reels],
  );

  const buildSegmentPayload = useCallback(
    () => ({ clipsPlayed: clipsPlayed.current, clipsAvailable: reels.length }),
    [reels.length],
  );

  const pendingAutoAdvanceRef = useRef<{ targetIndex: number } | null>(null);

  const handleAutoAdvance = useCallback(
    (index: number) => {
      pendingAutoAdvanceRef.current = { targetIndex: index };
      handleSetCurrentReelIndex(index);
    },
    [handleSetCurrentReelIndex],
  );

  const handleReelBarClick = useCallback(
    (index: number, isPaywalled: boolean) => {
      incrementClipsPlayedFor(index);
      handleSetCurrentReelIndex(index);
      if (isPaywalled) setShowReelPaywall(true);
    },
    [handleSetCurrentReelIndex, incrementClipsPlayedFor],
  );

  const handleNext = useCallback(() => {
    if (!reels[currentReelIndex + 1]?.highlightUrl) return;

    incrementClipsPlayedFor(currentReelIndex + 1);
    segmentEvent('next_wave', buildSegmentPayload());
    handleSetCurrentReelIndex(currentReelIndex + 1);
    if (reels[currentReelIndex + 1].highlightUrl === 'paywalled-highlight-reel')
      setShowReelPaywall(true);
  }, [
    buildSegmentPayload,
    currentReelIndex,
    handleSetCurrentReelIndex,
    incrementClipsPlayedFor,
    reels,
    segmentEvent,
  ]);

  const handlePrevious = useCallback(() => {
    if (!reels[currentReelIndex - 1]?.highlightUrl) return;

    incrementClipsPlayedFor(currentReelIndex - 1);
    segmentEvent('prev_wave', buildSegmentPayload());
    handleSetCurrentReelIndex(currentReelIndex - 1);
    if (reels[currentReelIndex - 1].highlightUrl === 'paywalled-highlight-reel')
      setShowReelPaywall(true);
  }, [
    buildSegmentPayload,
    currentReelIndex,
    handleSetCurrentReelIndex,
    incrementClipsPlayedFor,
    reels,
    segmentEvent,
  ]);

  const handleAdRunningChange = useCallback((adRunning: boolean) => {
    setAdRunning(adRunning);
    if (adRunning) {
      adWasRunningRef.current = true;
    } else if (adWasRunningRef.current) {
      adWasRunningRef.current = false;
      setAutoplayAllowed(true);
    }
  }, []);

  const handlePrerollResolvedChange = useCallback((resolved: boolean) => {
    setPrerollResolved(resolved);
  }, []);

  const handlePrerollSkippedChange = useCallback((skipped: boolean) => {
    if (skipped) setAutoplayAllowed(true);
  }, []);

  const handleInitAdContainerReady = useCallback((initAdContainer: () => void) => {
    initAdContainerRef.current = initAdContainer;
  }, []);

  const handlePlayOverlayClick = useCallback(() => {
    if (autoplayBlocked) {
      // Inside a fresh gesture — call play() directly so user activation reaches the media element.
      videoPlayerRef.current?.play();
      setAutoplayBlocked(false);
      setIsHighlightReelPlaying(true);
      return;
    }
    // Initialize the IMA ad container synchronously inside the click; state updates and the VAST fetch happen async.
    initAdContainerRef.current();
    setHasClickedPlayOverlay(true);
    setIsHighlightReelPlaying(true);
    incrementClipsPlayedFor(currentReelIndex);
    segmentEvent('play', buildSegmentPayload());
  }, [
    autoplayBlocked,
    buildSegmentPayload,
    currentReelIndex,
    incrementClipsPlayedFor,
    setIsHighlightReelPlaying,
    segmentEvent,
  ]);

  const handleAutoplayBlocked = useCallback(() => {
    setAutoplayBlocked(true);
  }, []);

  const handlePlayPauseButtonClick = useCallback(() => {
    if (videoPlayerRef.current?.isPlaying()) {
      videoPlayerRef.current?.pause();
      segmentEvent('pause', buildSegmentPayload());
      setIsHighlightReelPlaying(false);
    } else {
      videoPlayerRef.current?.play();
      segmentEvent('play', buildSegmentPayload());
      setIsHighlightReelPlaying(true);
    }
  }, [buildSegmentPayload, segmentEvent]);

  const handleSetShowEndOfReelsCTA = useCallback(() => {
    setShowReelPaywall(true);
  }, []);

  const handleReplayReelsButtonClick = useCallback(() => {
    setShowReelPaywall(false);
    setCurrentReelIndex(0);
    videoPlayerRef.current?.play();
    setIsHighlightReelPlaying(true);
  }, [setCurrentReelIndex, videoPlayerRef, setIsHighlightReelPlaying]);

  const handlePlayerOverlayMouseEnter = useCallback(() => {
    if (!showHostAd) {
      setShowHostAd(true);
    }
  }, [showHostAd]);

  const handlePlayerOverlayMouseLeave = useCallback(() => {
    if (showHostAd) {
      setShowHostAd(false);
    }
  }, [showHostAd]);

  const shouldShowVideoPlaylistPlayer = useMemo(() => {
    return (
      !showReelPaywall &&
      !isPaywalledReel &&
      !rewindFetchError &&
      !rewindsFetching &&
      reels.length > 0
    );
  }, [showReelPaywall, reels, isPaywalledReel, rewindFetchError, rewindsFetching]);

  // The click→ad-start gap: keep cam controls disabled and the spinner up so the user can't bypass the pre-roll.
  const awaitingPreroll = hasClickedPlayOverlay && !autoplayAllowed && !adRunning;
  const shouldDisplayPlayOverlay =
    shouldShowVideoPlaylistPlayer &&
    ((!hasClickedPlayOverlay && prerollResolved) || autoplayBlocked);
  const shouldDisplayPrerollLoading =
    shouldShowVideoPlaylistPlayer &&
    ((!hasClickedPlayOverlay && !prerollResolved) || awaitingPreroll);
  const shouldCountAsClipPlayed = reels[currentReelIndex]?.shouldCountAsClipPlayed ?? false;
  // Hide host attribution during ads — the highlights <video> stays paused for the ad's lifetime.
  const shouldDisplayHostAd = !adRunning && (showHostAd || !videoPlayerRef.current?.isPlaying());
  const hostAdOverlayClassName = isFullscreen
    ? styles.fullScreenHostAdContainer
    : styles.hostAdContainer;

  useEffect(() => {
    // When we reach the end of the non-paywalled reels as they auto play through we need to set the paywall to true.
    if (!showReelPaywall && reels[currentReelIndex]?.highlightUrl === 'paywalled-highlight-reel') {
      setShowReelPaywall(true);
    }
  }, [currentReelIndex, reels, showReelPaywall]);

  // Restart the cold-landing flow on cam change: re-arm pre-roll gating, reset reel position and click state.
  useEffect(() => {
    setHasClickedPlayOverlay(false);
    setIsHighlightReelPlaying(false);
    setPrerollResolved(!prerollEnabled);
    setAutoplayAllowed(!prerollEnabled);
    setAutoplayBlocked(false);
    adWasRunningRef.current = false;
    setCurrentReelIndex(0);
    clipsPlayed.current = 0;
    pendingAutoAdvanceRef.current = null;
  }, [camera?._id, prerollEnabled]);

  useEffect(() => {
    // Defer auto_advance tracking until any post-clip ad finishes — fire when the next clip actually plays.
    if (!pendingAutoAdvanceRef.current || adRunning) return undefined;
    const handle = window.setTimeout(() => {
      const pending = pendingAutoAdvanceRef.current;
      if (!pending) return;
      incrementClipsPlayedFor(pending.targetIndex);
      segmentEvent('auto_advance', buildSegmentPayload());
      pendingAutoAdvanceRef.current = null;
    }, 100);
    return () => window.clearTimeout(handle);
  }, [adRunning, buildSegmentPayload, currentReelIndex, incrementClipsPlayedFor, segmentEvent]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.playerWrapper} ref={fullscreenElementRef}>
        <CamHighlightsPill displayLabel={reels[currentReelIndex]?.displayLabel || ''} />
        {enableAds && (
          <ReelPlayerAds
            camera={camera}
            playerPlaying={isHighlightReelPlaying}
            currentReelIndex={currentReelIndex}
            shouldCountAsClipPlayed={shouldCountAsClipPlayed}
            shouldResetClipsPlayedCount={isPaywalledReel}
            onAdRunningChange={handleAdRunningChange}
            onPrerollResolvedChange={handlePrerollResolvedChange}
            onPrerollSkippedChange={handlePrerollSkippedChange}
            onInitAdContainerReady={handleInitAdContainerReady}
            spot={spot}
            prerollEnabled={prerollEnabled}
          />
        )}
        <Box
          className={styles.playerOverlayContainer}
          onMouseEnter={handlePlayerOverlayMouseEnter}
          onMouseLeave={handlePlayerOverlayMouseLeave}
        >
          {shouldDisplayHostAd && (
            <div className={hostAdOverlayClassName}>
              <CamHostAd spot={spot} camera={camera} className={styles.hostAdText} />
            </div>
          )}
          {enableCornerControls && !adRunning && (
            <Box className={styles.cornerControlsWrapper}>
              {!isFullscreen && (
                <CamFeedbackModal
                  camera={camera}
                  currentPlayer={Player.HIGHLIGHTS}
                  isWavePool={enabledFeatures.wavePool}
                  spot={spot}
                  timezone={timezone}
                />
              )}
              <FullscreenControl
                isFullscreen={isFullscreen}
                toggleFullscreen={handleRequestFullscreen}
                supportsFullscreen={supportsFullscreen}
              />
            </Box>
          )}
          {shouldShowVideoPlaylistPlayer && <div className={styles.reelBarsGradient} />}
          <ReelBars
            currentReelIndex={currentReelIndex}
            handleReelBarClick={handleReelBarClick}
            isFullScreen={isFullscreen}
            reels={reels}
          />
          {shouldShowVideoPlaylistPlayer && (
            <VideoPlaylistPlayer
              currentPlayer={currentReelIndex}
              playlist={reelUrls}
              setCurrentPlayer={handleAutoAdvance}
              setShowEndOfReelsCTA={handleSetShowEndOfReelsCTA}
              ref={videoPlayerRef}
              shouldAutoPlay={hasClickedPlayOverlay && autoplayAllowed && !autoplayBlocked}
              adRunning={adRunning}
              onAutoplayBlocked={handleAutoplayBlocked}
            />
          )}
          {isFullscreen && (
            <ReelPlayerCamControls
              isFirstReel={isFirstReel}
              isLastReel={isLastReel}
              adRunning={adRunning}
              isDisabled={
                shouldDisplayPlayOverlay ||
                showReelPaywall ||
                awaitingPreroll ||
                shouldDisplayPrerollLoading
              }
              playerPlaying={isHighlightReelPlaying}
              spot={spot}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onPlayPauseButtonClick={handlePlayPauseButtonClick}
            />
          )}
          {isFullscreen && showReelPaywall && (
            <EndOfReelsCTA upgradeUrl={upgradeUrl} onReplayClick={handleReplayReelsButtonClick} />
          )}
          {shouldDisplayPrerollLoading && (
            <div className={styles.loadingSpinnerContainer} data-testid="preroll-loading">
              <div className={styles.loadingSpinnerContent}>
                <LoadingSpinner />
              </div>
            </div>
          )}
          {shouldDisplayPlayOverlay && (
            <PlayButtonOverlay
              visible={shouldDisplayPlayOverlay}
              onClick={handlePlayOverlayClick}
            />
          )}
        </Box>
      </div>
      {!isFullscreen && (
        <ReelPlayerCamControls
          isFirstReel={isFirstReel}
          isLastReel={isLastReel}
          isDisabled={
            shouldDisplayPlayOverlay ||
            showReelPaywall ||
            awaitingPreroll ||
            shouldDisplayPrerollLoading
          }
          adRunning={adRunning}
          playerPlaying={isHighlightReelPlaying}
          spot={spot}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onPlayPauseButtonClick={handlePlayPauseButtonClick}
        />
      )}
      {!isFullscreen && showReelPaywall && (
        <EndOfReelsCTA upgradeUrl={upgradeUrl} onReplayClick={handleReplayReelsButtonClick} />
      )}
      {reels.length === 0 && !rewindsFetching && <EmptyReelsCTA upgradeUrl={upgradeUrl} />}
      {rewindsFetching && (
        <div className={styles.loadingSpinnerContainer}>
          <div className={styles.loadingSpinnerContent}>
            <LoadingSpinner />
          </div>
        </div>
      )}
      {rewindFetchError && (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <Typography variant="callout1" color="white">
              Something went wrong here.
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelPlayer;
